<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import type { CommunicatieFeaturesBerichtenBerichtDto as Bericht } from '@services/communicatie-public/orval-api/model/communicatieFeaturesBerichtenBerichtDto';
import VCallToAction from '@shared/component/VCallToAction/VCallToAction.vue';
import VCollapsibleSection from '@shared/component/VCollapsibleSection/VCollapsibleSection.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import VHeading from '@shared/typography/VHeading/VHeading.vue';
import { reactive } from 'vue';

export interface VBerichtenProps {
  berichten?: Bericht[];
  loading?: boolean;
}

const props = defineProps<VBerichtenProps>();

const state = reactive<{
  openBerichtId: number | null;
}>({
  openBerichtId: null,
});
</script>

<template>
  <template v-if="props.loading">
    <div class="border-b border-grey-200 p-4">
      <VSkeleton class="max-w-md" :timeout="0" />
    </div>

    <div class="p-4">
      <VSkeleton class="max-w-xs" :timeout="0" />
    </div>
  </template>

  <div v-else-if="!props.berichten?.length" class="pt-4 pb-2">
    <VCallToAction
      :icon="['fal', 'wind']"
      title="Er zijn geen prolongatiewijzigingen beschikbaar"
    />
  </div>

  <VCollapsibleSection
    v-else
    v-for="bericht in props.berichten"
    :key="bericht.id"
    :open="state.openBerichtId === bericht.id"
    @update:open="(isOpen) => (state.openBerichtId = isOpen ? bericht.id : null)"
  >
    <template #title>
      <div class="font-semibold">{{ bericht.titel }}</div>
    </template>

    <template #content>
      <div class="flex flex-col gap-4 p-4">
        <div class="text-sm" v-sanitized-html="bericht.inhoud"></div>

        <div v-if="bericht.polissen?.length" class="space-y-2">
          <VHeading :level="5">Relevant voor de volgende polis(sen)</VHeading>

          <div class="space-y-1">
            <div
              v-for="polis in bericht.polissen"
              :key="polis.polisnummer"
              class="flex flex-wrap items-center gap-x-3 gap-y-1 rounded border border-grey-200 bg-grey-50 p-2 text-sm"
            >
              <span class="font-medium">{{ polis.polisnummer }}</span>
              <span>{{ polis.omschrijvingSoortVerzekering }}</span>
              <span v-if="polis.autoDto.autoKenteken" class="text-xs text-grey-600">
                {{ polis.autoDto.autoKenteken }}
              </span>
              <span v-if="polis.autoDto.autoMerk" class="text-xs text-grey-600">{{
                polis.autoDto.autoMerk
              }}</span>
              <span v-if="polis.autoDto.autoModel" class="text-xs text-grey-600">
                {{ polis.autoDto.autoModel }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="bericht.aanvullendeBerichten?.length" class="space-y-2">
          <VHeading :level="5">Aanvullend bericht</VHeading>

          <div
            v-for="aanvullend in bericht.aanvullendeBerichten"
            :key="aanvullend.id"
            class="rounded border border-grey-200 bg-grey-50 text-sm"
          >
            <div class="p-3">
              <div class="font-medium">{{ aanvullend.titel }}</div>
              <div class="mt-1 whitespace-pre-line" v-sanitized-html="aanvullend.inhoud"></div>
            </div>
            <div v-if="aanvullend.polissen?.length" class="mt-3 space-y-1">
              <div
                v-for="polis in aanvullend.polissen"
                :key="polis.polisnummer"
                class="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-t-grey-200 p-3 text-sm"
              >
                <span class="font-medium">{{ polis.polisnummer }}</span>
                <span>{{ polis.omschrijvingSoortVerzekering }}</span>
                <span v-if="polis.autoDto.autoKenteken" class="text-xs text-grey-600">
                  {{ polis.autoDto.autoKenteken }}
                </span>
                <span v-if="polis.autoDto.autoMerk" class="text-xs text-grey-600">
                  {{ polis.autoDto.autoMerk }}
                </span>
                <span v-if="polis.autoDto.autoModel" class="text-xs text-grey-600">
                  {{ polis.autoDto.autoModel }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="bericht.bestanden?.length" class="space-y-2">
          <VHeading :level="5">Bijlagen</VHeading>

          <div class="flex flex-col gap-1">
            <a
              v-for="bestand in bericht.bestanden"
              :key="bestand.id"
              :href="`/net/documents/DownloadFile?id=${bestand.id}`"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              <FontAwesomeIcon icon="far fa-file-pdf" class="text-red-600" />
              {{ bestand.naam }}
            </a>
          </div>
        </div>
      </div>
    </template>
  </VCollapsibleSection>
</template>
